/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { CustomerModel } from "../models";

type PermissionsSchema = {
    text: string;
    children: PermissionsSchema[];
}

const CAN_REQUEST_QUOTE_PERMISSION = 'All/Quotes/View/Request, Edit, Delete';
const CAN_EDIT_QUOTE_PERMISSION = 'All/Quotes/View/Request, Edit, Delete';
const CAN_DELETE_QUOTE_PERMISSION = 'All/Quotes/View/Request, Edit, Delete';

/**
 * Flatten the permissions into a list of strings
 * @param permissions - The permissions to flatten
 * @returns A list of strings representing the flattened permissions
 */
export const flattenPermissions = (permissions: PermissionsSchema[]): string[] => {
    const result: string[] = [];
    
    // Recursively traverse the permissions and build the path
    const traverse = (nodes: PermissionsSchema[], path: string[] = []) => {
        for (const node of nodes) {
            const currentPath = [...path, node.text];
            
            if (node.children && node.children.length > 0) {
                traverse(node.children, currentPath);
            } else {
                result.push(currentPath.join('/'));
            }
        }
    };
    
    traverse(permissions);
    return result;
}

export function transformCustomer(customerData: any): CustomerModel {
    const { role: { permissions } } = customerData;
    const flattenedPermissions = flattenPermissions(permissions);
    return {
        permissions: {
            canRequestQuote: flattenedPermissions.includes(CAN_REQUEST_QUOTE_PERMISSION),
            canEditQuote: flattenedPermissions.includes(CAN_EDIT_QUOTE_PERMISSION),
            canDeleteQuote: flattenedPermissions.includes(CAN_DELETE_QUOTE_PERMISSION),
        }
    }
}